import { c as _c } from "react/compiler-runtime";
// Общие кусочки раздела «Загрузка каталога»: статусы, форматы, иконки файлов,
// шаги мастера, превью картинок задания.
import { Check, Download, File as FileIcon, FileArchive, FileImage, FileSpreadsheet, FileText, FileType, Loader2 } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { CatalogImportFileKind, CatalogImportFileStatus, CatalogImportIssue, CatalogImportItemStatus, CatalogImportJob, CatalogImportJobStatus, CatalogImportVariantDraft } from "@osmix/contracts";
import { CATALOG_IMPORT_JOB_STATUS_LABELS } from "@osmix/contracts";
import { api } from "@/api";
import { useImportFileUrl } from "@/api/hooks/use-catalog-import";
import { useSellers } from "@/api/hooks/use-sellers";
import { useStorefronts } from "@/api/hooks/use-storefronts";
import { Button } from "@osmix/ui/button";
import { cn, formatPrice } from "@/lib/utils";

// ── Форматы ──────────────────────────────────────────────────────────────

const usd = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
const compact = new Intl.NumberFormat("ru-RU", {
  notation: "compact",
  maximumFractionDigits: 1
});

/** Расход на модель: «0,38 $»; копейки центов не показываем. */
export const formatUsd = (value: number) => value > 0 && value < 0.01 ? "< 0,01 $" : usd.format(value);
export const formatTokens = (value: number) => compact.format(value);
export const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} Б`;
  const units = ["КБ", "МБ", "ГБ"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toLocaleString("ru-RU", {
    maximumFractionDigits: value < 10 ? 1 : 0
  })} ${units[unit]}`;
};

/** ДД.ММ.ГГГГ — как во всей консоли. */
export const formatDate = (iso: string | null | undefined) => iso ? new Date(iso).toLocaleDateString("ru-RU") : "—";
export const formatDateTime = (iso: string | null | undefined) => iso ? `${new Date(iso).toLocaleDateString("ru-RU")}, ${new Date(iso).toLocaleTimeString("ru-RU", {
  hour: "2-digit",
  minute: "2-digit"
})}` : "—";
export const errorText = (e: unknown) => e instanceof Error ? e.message : String(e);

/** Цена позиции: одна — «4 990 ₽», разные по размерам — «4 990–5 490 ₽». */
export const priceRange = (variants: CatalogImportVariantDraft[]) => {
  const prices = variants.map(v => v.price).filter((p): p is number => p != null);
  if (prices.length === 0) return "—";
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatPrice(min) : `${formatPrice(min).replace(/\s?₽$/, "")}–${formatPrice(max)}`;
};

/** «1 товар», «2 товара», «5 товаров». */
export const plural = (n: number, one: string, few: string, many: string) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};
const uniq = (values: string[]) => [...new Set(values.map(v => v.trim()).filter(Boolean))];

/** «S, M, L · белый, чёрный» — что продаётся в позиции. */
export const variantSummary = (variants: CatalogImportVariantDraft[]) => {
  const sizes = uniq(variants.map(v => v.size));
  const colors = uniq(variants.map(v => v.color));
  return [sizes.join(", "), colors.join(", ")].filter(Boolean).join(" · ") || "—";
};
export const stockSum = (variants: CatalogImportVariantDraft[]) => {
  const known = variants.map(v => v.stock).filter((s): s is number => s != null);
  return known.length ? known.reduce((a, b) => a + b, 0) : null;
};

/** Куда идёт загрузка — одной строкой для списка и шапки задания. */
export const targetText = (job: Pick<CatalogImportJob, "target" | "shop" | "application">) => {
  if (job.target.kind === "existing_shop") return job.shop?.name ?? "Магазин";
  if (job.shop) return `${job.shop.name} (новый)`;
  if (job.application) return `Новый магазин · заявка «${job.application.shopName}»`;
  const c = job.target.contact;
  const who = c?.name || c?.phone || c?.email;
  return who ? `Новый магазин · ${who}` : "Новый магазин";
};

// ── Статусы ──────────────────────────────────────────────────────────────

const JOB_DOT: Record<CatalogImportJobStatus, string> = {
  draft: "bg-muted-foreground",
  parsing: "bg-sky-500 animate-pulse",
  review: "bg-amber-500",
  committing: "bg-sky-500 animate-pulse",
  done: "bg-emerald-500",
  failed: "bg-destructive",
  cancelled: "bg-muted-foreground/40"
};
function Pill(t0) {
  const $ = _c(10);
  const {
    dot,
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("inline-flex w-fit shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== dot) {
    t2 = cn("size-1.5 rounded-full", dot);
    $[2] = dot;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className={t2} aria-hidden={true} />;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== children || $[7] !== t1 || $[8] !== t3) {
    t4 = <span className={t1}>{t3}{children}</span>;
    $[6] = children;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function JobStatusPill(t0) {
  const $ = _c(4);
  const {
    status
  } = t0;
  const t1 = JOB_DOT[status];
  const t2 = status === "cancelled" ? "text-muted-foreground" : undefined;
  const t3 = CATALOG_IMPORT_JOB_STATUS_LABELS[status];
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3) {
    t4 = <Pill dot={t1} className={t2}>{t3}</Pill>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}
export const ITEM_STATUS_LABELS: Record<CatalogImportItemStatus, string> = {
  ready: "Готово",
  warning: "Проверить",
  error: "Ошибка",
  excluded: "Исключено",
  committed: "Загружено",
  failed: "Не загрузилось"
};
const ITEM_DOT: Record<CatalogImportItemStatus, string> = {
  ready: "bg-emerald-500",
  warning: "bg-amber-500",
  error: "bg-destructive",
  excluded: "bg-muted-foreground/40",
  committed: "bg-emerald-500",
  failed: "bg-destructive"
};
export function ItemStatusPill(t0) {
  const $ = _c(8);
  const {
    status
  } = t0;
  const t1 = ITEM_DOT[status];
  const t2 = status === "excluded" && "text-muted-foreground";
  const t3 = (status === "error" || status === "failed") && "border-destructive/40 text-destructive";
  const t4 = status === "warning" && "border-amber-500/40 text-amber-700 dark:text-amber-500";
  let t5;
  if ($[0] !== t2 || $[1] !== t3 || $[2] !== t4) {
    t5 = cn(t2, t3, t4);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = ITEM_STATUS_LABELS[status];
  let t7;
  if ($[4] !== t1 || $[5] !== t5 || $[6] !== t6) {
    t7 = <Pill dot={t1} className={t5}>{t6}</Pill>;
    $[4] = t1;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
}
export const FILE_STATUS_LABELS: Record<CatalogImportFileStatus, string> = {
  pending: "Ждёт разбора",
  parsed: "Прочитан",
  skipped: "Пропущен",
  failed: "Не прочитан"
};
export const FILE_KIND_LABELS: Record<CatalogImportFileKind, string> = {
  spreadsheet: "Таблица",
  document: "Документ",
  pdf: "PDF",
  image: "Фото",
  text: "Текст",
  archive: "Архив",
  other: "Файл"
};
const KIND_ICON: Record<CatalogImportFileKind, typeof FileIcon> = {
  spreadsheet: FileSpreadsheet,
  document: FileText,
  pdf: FileType,
  image: FileImage,
  text: FileText,
  archive: FileArchive,
  other: FileIcon
};
export function FileKindIcon(t0) {
  const $ = _c(6);
  const {
    kind,
    className
  } = t0;
  const Icon = KIND_ICON[kind];
  let t1;
  if ($[0] !== className) {
    t1 = cn("size-4 shrink-0 text-muted-foreground", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = FILE_KIND_LABELS[kind];
  let t3;
  if ($[2] !== Icon || $[3] !== t1 || $[4] !== t2) {
    t3 = <Icon className={t1} aria-label={t2} />;
    $[2] = Icon;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function IssueList(t0) {
  const $ = _c(7);
  const {
    issues,
    className
  } = t0;
  if (issues.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col gap-1", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== issues) {
    t2 = issues.map(_temp);
    $[2] = issues;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <ul className={t1}>{t2}</ul>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

// ── Мелкие блоки ─────────────────────────────────────────────────────────
function _temp(issue, i) {
  return <li key={`${issue.field ?? ""}-${i}`} className="flex gap-2 text-sm"><span className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", issue.level === "error" ? "bg-destructive" : "bg-amber-500")} aria-hidden={true} /><span className={issue.level === "error" ? "text-destructive" : "text-amber-700 dark:text-amber-500"}>{issue.message}</span></li>;
}
export function ProgressBar(t0) {
  const $ = _c(11);
  const {
    value,
    max,
    className
  } = t0;
  let t1;
  if ($[0] !== max || $[1] !== value) {
    t1 = max > 0 ? Math.min(100, Math.round(value / max * 100)) : 0;
    $[0] = max;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const pct = t1;
  let t2;
  if ($[3] !== className) {
    t2 = cn("h-2 w-full overflow-hidden rounded-full bg-muted", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `${pct}%`;
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{
      width: t3
    }} />;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== pct || $[8] !== t2 || $[9] !== t4) {
    t5 = <div className={t2} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>{t4}</div>;
    $[7] = pct;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
export function StatTile(t0) {
  const $ = _c(14);
  const {
    label,
    value,
    tone: t1,
    hint
  } = t0;
  const tone = t1 === undefined ? "default" : t1;
  const t2 = tone === "good" && "text-emerald-600 dark:text-emerald-500";
  const t3 = tone === "warning" && "text-amber-700 dark:text-amber-500";
  const t4 = tone === "bad" && "text-destructive";
  const t5 = tone === "muted" && "text-muted-foreground";
  let t6;
  if ($[0] !== t2 || $[1] !== t3 || $[2] !== t4 || $[3] !== t5) {
    t6 = cn("text-base font-semibold tabular-nums", t2, t3, t4, t5);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t6 || $[6] !== value) {
    t7 = <div className={t6}>{value}</div>;
    $[5] = t6;
    $[6] = value;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== label) {
    t8 = <div className="truncate text-xs text-muted-foreground">{label}</div>;
    $[8] = label;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== hint || $[11] !== t7 || $[12] !== t8) {
    t9 = <div className="min-w-0 rounded-lg border p-3" title={hint}>{t7}{t8}</div>;
    $[10] = hint;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}
export type WizardStep = "materials" | "parse" | "review" | "commit";

/** Шаги мастера: пройденные и доступные — кнопки, недоступные — подписи. */
export function StepHeader(t0) {
  const $ = _c(16);
  const {
    steps,
    current,
    viewing,
    onSelect,
    complete: t1
  } = t0;
  const complete = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== complete || $[1] !== current || $[2] !== steps) {
    t2 = complete ? steps.length : steps.findIndex(s => s.key === current);
    $[0] = complete;
    $[1] = current;
    $[2] = steps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const currentIndex = t2;
  let t3;
  if ($[4] !== currentIndex || $[5] !== onSelect || $[6] !== steps || $[7] !== viewing) {
    let t4;
    if ($[9] !== currentIndex || $[10] !== onSelect || $[11] !== steps.length || $[12] !== viewing) {
      t4 = (step, index) => {
        const passed = index < currentIndex;
        const active = step.key === viewing;
        return <li key={step.key} className="flex shrink-0 items-center gap-1 sm:gap-2"><button type="button" disabled={!step.reachable} aria-current={active ? "step" : undefined} onClick={() => onSelect(step.key)} className={cn("flex items-center gap-2 rounded-full px-2 py-1 text-sm transition-colors enabled:hover:bg-muted disabled:cursor-default", active && "bg-muted font-medium", !step.reachable && "text-muted-foreground")}><span className={cn("grid size-6 place-items-center rounded-full border text-xs font-semibold", passed && "border-primary bg-primary text-primary-foreground", index === currentIndex && "border-primary")}>{passed ? <Check className="size-3.5" /> : index + 1}</span><span className={cn(!active && "max-sm:sr-only")}>{step.label}</span></button>{index < steps.length - 1 && <span className="h-px w-3 bg-border sm:w-6" aria-hidden={true} />}</li>;
      };
      $[9] = currentIndex;
      $[10] = onSelect;
      $[11] = steps.length;
      $[12] = viewing;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    t3 = steps.map(t4);
    $[4] = currentIndex;
    $[5] = onSelect;
    $[6] = steps;
    $[7] = viewing;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[14] !== t3) {
    t4 = <ol className="flex max-w-full items-center gap-1 overflow-x-auto pb-1 sm:gap-2" aria-label={"\u0428\u0430\u0433\u0438 \u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0438"}>{t3}</ol>;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}

/** Шаблон xlsx «куда заполнять» — та же кнопка на списке и в материалах. */
export function TemplateDownload({
  variant = "outline",
  className
}: {
  variant?: "outline" | "link";
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const {
        blob,
        filename
      } = await api.catalogImport.template();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return <span className={cn("inline-flex flex-col", className)}>
			<Button type="button" variant={variant} size={variant === "link" ? "inline" : "default"} onClick={() => void download()} disabled={busy} className={variant === "link" ? "h-auto w-fit" : undefined}>
				{busy ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
				Скачать шаблон xlsx
			</Button>
			{error && <span className="text-xs text-destructive">{error}</span>}
		</span>;
}

/** Виден ли элемент — превью грузятся, только когда до них долистали. */
function useInView(t0) {
  const $ = _c(6);
  const rootMargin = t0 === undefined ? "200px" : t0;
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  let t1;
  let t2;
  if ($[0] !== rootMargin || $[1] !== seen) {
    t1 = () => {
      const el = ref.current;
      if (!el || seen) {
        return;
      }
      if (typeof IntersectionObserver === "undefined") {
        setSeen(true);
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries.some(_temp2)) {
          setSeen(true);
        }
      }, {
        rootMargin
      });
      observer.observe(el);
      return () => observer.disconnect();
    };
    t2 = [seen, rootMargin];
    $[0] = rootMargin;
    $[1] = seen;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== seen) {
    t3 = {
      ref,
      seen
    };
    $[4] = seen;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/** Превью картинки задания (байты — через шлюз с токеном). */
function _temp2(e) {
  return e.isIntersecting;
}
export function ImportThumb(t0) {
  const $ = _c(13);
  const {
    jobId,
    fileId,
    url: external,
    alt: t1,
    className
  } = t0;
  const alt = t1 === undefined ? "" : t1;
  const {
    ref,
    seen
  } = useInView();
  const {
    url,
    error,
    isLoading
  } = useImportFileUrl(jobId, fileId, seen && !external);
  const [broken, setBroken] = useState(null);
  const src = external ?? url;
  let t2;
  if ($[0] !== className) {
    t2 = cn("relative size-10 shrink-0 overflow-hidden rounded bg-muted", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== alt || $[3] !== broken || $[4] !== error || $[5] !== fileId || $[6] !== isLoading || $[7] !== src) {
    t3 = src && !error && broken !== src ? <img src={src} alt={alt} className="size-full object-cover" loading="lazy" onError={() => setBroken(src)} /> : fileId && isLoading && <span className="absolute inset-0 animate-pulse bg-muted" aria-hidden={true} />;
    $[2] = alt;
    $[3] = broken;
    $[4] = error;
    $[5] = fileId;
    $[6] = isLoading;
    $[7] = src;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== ref || $[10] !== t2 || $[11] !== t3) {
    t4 = <div ref={ref} className={t2}>{t3}</div>;
    $[9] = ref;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}

/**
 * Ссылка на магазин в консоли: у опубликованного — витрина по slug, у
 * продавца без витрины — карточка продавца (магазином служит его id).
 */
export function useShopHref(shopId) {
  const storefronts = useStorefronts().data ?? [];
  const sellers = useSellers().data ?? [];
  if (!shopId) {
    return null;
  }
  const sf = storefronts.find(s => s.id === shopId);
  if (sf) {
    return `/shops/${sf.slug}`;
  }
  const seller = sellers.find(s_0 => s_0.shopId === shopId || s_0.id === shopId);
  return `/shops/seller/${seller?.id ?? shopId}`;
}